import { ChevronDown } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { useFormFieldContext } from './form-field-context';

export interface SelectProps extends Omit<ComponentPropsWithRef<'select'>, 'size'> {
  selectSize?: 'sm' | 'md' | 'lg';
  /** Ikona po lewej stronie (np. herb dyscypliny albo pinezka lokalizacji). */
  leadingIcon?: ReactNode;
  invalid?: boolean;
  /** Tekst zastepczy pokazywany, gdy nic nie wybrano. Renderowany jako wylaczona opcja. */
  placeholder?: string;
  children: ReactNode;
}

const sizeClasses = {
  sm: 'h-9 pl-3 pr-9 text-[0.8125rem]',
  md: 'h-11 pl-3.5 pr-10 text-sm',
  lg: 'h-13 pl-4 pr-10 text-sm',
} as const;

/**
 * Lista rozwijana oparta na natywnym `<select>`.
 *
 * Natywna kontrolka daje za darmo poprawne zachowanie na urzadzeniach dotykowych
 * i pelna dostepnosc. Do zlozonych przypadkow (wyszukiwanie, wielokrotny wybor,
 * awatary w opcjach) zbudujemy osobny komponent — nie rozbudowujemy tego.
 */
export function Select({
  className,
  selectSize = 'md',
  leadingIcon,
  invalid,
  placeholder,
  children,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  required,
  defaultValue,
  value,
  ...props
}: SelectProps) {
  const field = useFormFieldContext();
  const resolvedInvalid = invalid ?? ariaInvalid ?? field?.invalid ?? false;

  // Gdy jest placeholder i nie podano wartosci, zaczynamy od pustej opcji.
  const hasControlledValue = value !== undefined;
  const resolvedDefaultValue =
    defaultValue ?? (placeholder && !hasControlledValue ? '' : undefined);

  return (
    <div className="relative flex items-center">
      {leadingIcon && (
        <span
          aria-hidden="true"
          className="text-fg-subtle pointer-events-none absolute left-3.5 flex items-center [&_svg]:size-4"
        >
          {leadingIcon}
        </span>
      )}

      <select
        id={id ?? field?.controlId}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
        aria-invalid={resolvedInvalid || undefined}
        required={required ?? field?.required}
        {...(hasControlledValue ? { value } : {})}
        {...(resolvedDefaultValue !== undefined ? { defaultValue: resolvedDefaultValue } : {})}
        className={cn(
          'w-full min-w-0 appearance-none',
          'bg-surface-raised text-fg border-border-input rounded-lg border',
          'transition-[border-color,box-shadow] duration-150',
          'focus-visible:border-brand focus-visible:ring-brand/35 focus-visible:ring-2 focus-visible:outline-none',
          'disabled:bg-surface-sunken disabled:text-fg-disabled disabled:cursor-not-allowed',
          'aria-invalid:border-danger-500 aria-invalid:focus-visible:ring-danger-500/30',
          sizeClasses[selectSize],
          leadingIcon && 'pl-10',
          className,
        )}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {children}
      </select>

      <ChevronDown
        aria-hidden="true"
        className="text-fg-subtle pointer-events-none absolute right-3.5 size-4"
      />
    </div>
  );
}
